import React, { useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import toaster from 'react-hot-toast';
import styles from 'components/admin/deviceDashboard/deviceEditor/DeviceSimulator.module.scss';
import AllianceSerialDeviceInitializationSimulator from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/AllianceSerialInitializationSimulator';
import DexterDeviceInitializationSimulator from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/DexterInitializationSimulator';
import { ElectroluxSerialDeviceInitializationSimulator } from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/ElectroluxSerialInitializationSimulator';
// components and styling
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { MACHINE_MANUFACTURERS } from 'utils/constants';
import { getMachineManufacturers } from 'api/admin/devices/devices';

const DeviceInitializationSimulator = (props) => {
  const { device, onBack, onSubmit } = props;

  // form data
  const [manufacturer, setManufacturer] = useState(null);
  const [machineStyles, setMachineStyles] = useState(null);
  const [controlTiers, setControlTiers] = useState(null);

  // wizard screen states
  const [showManufacturerSelection, setShowManufacturerSelection] = useState(true);

  /**
   * fetch the list of machine manufacturers on initialization
   */
  const { data: manufacturers, isLoading } = useQuery(
    ['getMachineManufacturers'],
    async () => {
      const { data } = await getMachineManufacturers();
      return data.details;
    },
    {
      onError: (error) => {
        toaster(`Error fetching machine manufacturers: ${error?.response?.data?.error}`);
      },
    },
  );

  /**
   * when clicking next on the manufacturer selection, show the machine style selection UI
   * and hide the manufacturer selection UI
   */
  const handleNextOnManufacturerSelection = () => {
    setShowManufacturerSelection(false);
  };

  /**
   * when selecting the manufacturer in the dropdown, set the manufacturer name and
   * specific machine styles belonging to that manufacturer in state
   *
   * @param {object} manufacturerData
   */
  const handleManufacturerSelection = (manufacturerData) => {
    setManufacturer(manufacturerData.manufacturer);
    setMachineStyles(manufacturerData.styles);
    setControlTiers(manufacturerData.controlTiers);
  };

  /**
   * render the manufacturer selection required for initializing a device
   */
  const renderManufacturerSelection = () => {
    return (
      <>
        <header className={styles.header}>
          <h4>Choose your manufacturer</h4>
          <p>Choose the manufacturer you want the device to be paired with</p>
        </header>
        <main className={styles.main}>
          <section className={styles.section}>
            <p>Choose the manufacturer</p>
            <Autocomplete
              disablePortal
              id="autocomplete-choose-manufacturer"
              getOptionLabel={(option) => option.manufacturer ?? option}
              options={manufacturers}
              sx={{ width: '300px' }}
              onChange={(event, value) => {
                handleManufacturerSelection(value);
              }}
              renderInput={(params) => <TextField {...params} label="Manufacturer" />}
              value={manufacturer}
              isOptionEqualToValue={(option, value) => option.manufacturer === value}
            />
          </section>
        </main>
        <footer className={styles.footer}>
          <p className={styles.backButton} onClick={onBack}>
            Back
          </p>
          <button
            className={classNames(styles.backButton, {
              disabled: !manufacturer,
            })}
            disabled={!manufacturer}
            onClick={handleNextOnManufacturerSelection}
          >
            Next
          </button>
        </footer>
      </>
    );
  };

  /**
   * Display the manufacturer-specific initialization wizards.
   */
  const handleManufacturerInitializationRendering = () => {
    if (manufacturer === MACHINE_MANUFACTURERS.DEXTER) {
      return (
        <DexterDeviceInitializationSimulator
          device={device}
          manufacturer={manufacturer}
          onBack={() => {
            setShowManufacturerSelection(true);
          }}
          onSubmit={onSubmit}
        />
      );
    } else if (manufacturer === MACHINE_MANUFACTURERS.ELECTROLUX) {
      return (
        <ElectroluxSerialDeviceInitializationSimulator
          device={device}
          manufacturer={manufacturer}
          controlTiers={controlTiers}
          machineStyles={machineStyles}
          onBack={() => {
            setShowManufacturerSelection(true);
          }}
          onSubmit={onSubmit}
        />
      );
    } else {
      return (
        <AllianceSerialDeviceInitializationSimulator
          device={device}
          manufacturer={manufacturer}
          controlTiers={controlTiers}
          machineStyles={machineStyles}
          onBack={() => {
            setShowManufacturerSelection(true);
          }}
          onSubmit={onSubmit}
        />
      );
    }
  };

  /**
   * Determine the proper form/wizard step based on state of the component
   */
  const renderInitializationWizardOptions = () => {
    if (showManufacturerSelection) {
      return renderManufacturerSelection();
    }

    return handleManufacturerInitializationRendering();
  };

  return <>{isLoading ? <CentsLoader /> : renderInitializationWizardOptions()}</>;
};

export default DeviceInitializationSimulator;
